import clsx from 'clsx'
import React, { useEffect, useState, useRef, useMemo } from 'react'

import { Icon } from '@/components/Icon'
import { PurchaseTypes } from '@/constants/programs'
import type { SelectedOptionsState } from '@/hooks/useFlexibleProgram'
import {
  DeliveryZoneException,
  type Delivery,
  type DeliveryZone,
} from '@/models/delivery-zone'
import { loadAndGetDeliveryZoneByZip } from '@/stores/delivery-zone/operations'
import { getAvailableWeeksByZipCode } from '@/stores/packages/operations'
import type {
  CMSHolidaySchedule,
  CMSProgramDeliveryOption,
} from '@/types/sanity'
import dayjs from '@/utilities/dayjs-config'
import type { Dayjs } from '@/utilities/dayjs-config'
import { captureException } from '@/utilities/logger-fe'
import { getDaysOfFoodFromSku } from '@/utilities/sakara'

import { DeliveryMessage } from './DeliveryMessage'
import type { DeliveryDataState } from './Form'

import './DeliveryWindow.scss'

type DeliveryWindowProps = {
  cmsConfig: CMSProgramDeliveryOption
  selectedOptions: SelectedOptionsState
  onDeliveryDataChange?: (data: DeliveryDataState) => void
}

type StartDateListItem = {
  date: Dayjs
  label: string
  isDisabled: boolean
  weekDeliveryZone: DeliveryZone
  holidaySchedule?: CMSHolidaySchedule
}

export const DeliveryWindow: React.FC<DeliveryWindowProps> = ({
  cmsConfig,
  selectedOptions,
  onDeliveryDataChange,
}) => {
  const {
    deliveryMessageTemplate,
    deliveryWindowDescription,
    oosDates,
    holidaySchedules,
  } = cmsConfig

  const [zipCode, setZipCode] = useState('')
  const [isZipCodeApplied, setIsZipCodeApplied] = useState(false)
  const [isZipCodeValid, setIsZipCodeValid] = useState(true)
  const [visibleStartDates, setVisibleStartDates] = useState<
    StartDateListItem[]
  >([])
  const [selectedStartDate, setSelectedStartDate] = useState<
    StartDateListItem | undefined
  >()
  const [deliveryZone, setDeliveryZone] = useState<DeliveryZone | undefined>()

  const zipCodeInputRef = useRef<HTMLInputElement>(null)

  const weekDeliveryZone = selectedStartDate?.weekDeliveryZone
  const holidaySchedule = selectedStartDate?.holidaySchedule

  const downgradedSelectedOptions = useMemo<
    SelectedOptionsState | undefined
  >(() => {
    if (!holidaySchedule) return

    const downgradedVariant = holidaySchedule.downgrades.find(
      (d) => d.from.id === selectedOptions?.activeVariant?.id,
    )?.to
    if (!downgradedVariant) return

    const selectedDays = selectedOptions?.days
    const downgradedDays = getDaysOfFoodFromSku(downgradedVariant.sku)
    const selectedDaysOption = selectedOptions?.daysOption
    // TODO: refactor this
    const downgradedDaysOption =
      selectedDays && selectedDaysOption
        ? {
            ...selectedDaysOption,
            label: selectedDaysOption.label.replace(
              RegExp(selectedDays.toString(), 'g'),
              downgradedDays.toString(),
            ),
          }
        : undefined

    const downgradedSelectedOptions = {
      ...selectedOptions,
      days: downgradedDays,
      daysOption: downgradedDaysOption,
      activeVariant: downgradedVariant,
    }
    return downgradedSelectedOptions
  }, [selectedOptions, holidaySchedule])

  const deliveries = useMemo<Delivery[]>(() => {
    const { days, meals } = downgradedSelectedOptions || selectedOptions
    if (!weekDeliveryZone || !days || !meals?.length) return []

    return weekDeliveryZone.getDeliveriesForDaysOfFoodAndMeals(days, meals)
  }, [weekDeliveryZone, selectedOptions, downgradedSelectedOptions])

  const exceptionNotification = useMemo<string | null>(() => {
    if (
      selectedOptions.purchaseType !== PurchaseTypes.SUBSCRIPTION ||
      !(weekDeliveryZone instanceof DeliveryZoneException) ||
      !deliveryZone ||
      !selectedStartDate ||
      !selectedOptions.days
    )
      return null

    const daysOfMealsDelivered = weekDeliveryZone.totalDeliverableMeals
    const deliveryDays = deliveries.map((d) => d.day).join(' and ')
    const paymentNotice = deliveryZone.isDaysOfFoodChangedForWeek(
      selectedOptions.days,
      selectedStartDate.date,
    )
      ? 'Your payment will be adjusted accordingly for the shortened week.'
      : ''
    const message = `
      *Due to the holiday schedule, you will receive ${daysOfMealsDelivered} days of meals arriving on ${deliveryDays}.
      ${paymentNotice}
      `

    return message
  }, [
    weekDeliveryZone,
    deliveryZone,
    selectedStartDate,
    deliveries,
    selectedOptions,
  ])

  const ctaZipCode = isZipCodeApplied ? 'edit' : 'go'

  useEffect(() => {
    const persistedZipCode = window.localStorage.getItem('sakara-zip')
    if (persistedZipCode) {
      setZipCode(persistedZipCode)
    }
  }, [])

  useEffect(() => {
    if (!zipCode || !isZipCodeValid) return

    const timeout = setTimeout(async () => {
      const result = await loadAndGetDeliveryZoneByZip(zipCode)
      setDeliveryZone(result)
      if (result) {
        setIsZipCodeValid(true)
        window.localStorage.setItem('sakara-zip', zipCode)
      } else {
        setIsZipCodeValid(false)
      }
      setIsZipCodeApplied(true)
    }, 200)

    return () => {
      clearTimeout(timeout)
    }
  }, [zipCode])

  useEffect(() => {
    ;(async () => {
      const { days, meals } = selectedOptions
      if (!deliveryZone || !days || !meals?.length) return

      const allStartDates = await getAllStartDates()
      const _visibleStartDates = allStartDates
        .map((date) => {
          const isHolidayWeek = deliveryZone.isScheduleChanged(days, date)
          const isOosDate = (oosDates || []).some((oosDate) =>
            date.isSame(oosDate, 'day'),
          )
          const label = `${date.format('dddd, MMMM D')}${
            isOosDate ? ' (Sold Out)' : ''
          }${isHolidayWeek ? '*' : ''}`
          const weekDeliveryZone = deliveryZone.getDeliveryZoneForWeek(date)

          const holidaySchedule =
            weekDeliveryZone instanceof DeliveryZoneException
              ? holidaySchedules?.find(
                  (hs) =>
                    weekDeliveryZone.week.isSame(hs.week, 'week') &&
                    hs.deliveryZoneIds.includes(weekDeliveryZone.id),
                )
              : undefined

          return {
            date,
            label,
            isDisabled: isOosDate,
            weekDeliveryZone,
            holidaySchedule,
          }
        })
        .filter(({ date, holidaySchedule }) => {
          if (
            !deliveryZone.isDaysOfFoodChangedForWeek(days, date) ||
            selectedOptions.purchaseType === PurchaseTypes.PACK_OF_WEEKS
          )
            return true

          return holidaySchedule?.downgrades.some(
            (d) => d.from.id === selectedOptions.activeVariant?.id,
          )
        })

      const selectableStartDates = _visibleStartDates.filter(
        ({ isDisabled }) => !isDisabled,
      )
      const _selectedStartDate =
        selectableStartDates.find(({ date }) =>
          date.isSame(selectedStartDate?.date, 'week'),
        ) || selectableStartDates[0]

      setVisibleStartDates(_visibleStartDates)
      setSelectedStartDate(_selectedStartDate)
    })()
  }, [deliveryZone, selectedOptions])

  useEffect(() => {
    onDeliveryDataChange?.({
      zipCode: isZipCodeValid && isZipCodeApplied ? zipCode : undefined,
      startDate: selectedStartDate?.date,
      deliveryZone: weekDeliveryZone,
      deliveries,
      downgradedSelectedOptions,
    })
  }, [
    zipCode,
    isZipCodeValid,
    isZipCodeApplied,
    selectedStartDate,
    weekDeliveryZone,
    deliveries,
  ])

  const getAllStartDates = async () => {
    const { days, meals } = selectedOptions
    if (!deliveryZone || !days || !meals?.length) return []

    try {
      return selectedOptions.purchaseType === PurchaseTypes.PACK_OF_WEEKS
        ? await getAvailableWeeksByZipCode(zipCode)
        : deliveryZone.getStartDates({
            daysOfFood: days,
            meals: meals,
            useExceptions: true,
          })
    } catch (error) {
      captureException(error)
      return []
    }
  }

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newZipCode = e.target.value
    setZipCode(newZipCode)
    setIsZipCodeApplied(false)
    if (!/^[0-9]{5}$/.test(newZipCode)) {
      setIsZipCodeValid(false)
      setIsZipCodeApplied(true)
      return
    } else {
      setIsZipCodeValid(true)
    }
  }

  const handleZipCodeCtaClick = () => {
    zipCodeInputRef.current?.focus()
    zipCodeInputRef.current?.select()
  }

  const handleStartDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStartDate = e.target.value
      ? visibleStartDates.find(({ date }) =>
          dayjs.tz(e.target.value).isSame(date, 'day'),
        )
      : undefined
    setSelectedStartDate(newStartDate)
  }

  const zipCodeInputWrapperClasses = [
    'delivery-window__input-wrapper',
    !isZipCodeValid && isZipCodeApplied && 'delivery-window__input--error',
  ]

  const startDateWrapperClasses = [
    'delivery-window__item',
    (!isZipCodeValid || !isZipCodeApplied) && 'delivery-window__item--disabled',
  ]

  return (
    <div className="delivery-window">
      <div className="delivery-window__item">
        <label
          className="delivery-window__label type-eyebrow"
          htmlFor="input-delivery-zip-code"
        >
          Delivery Zip Code
        </label>
        <div className={clsx(zipCodeInputWrapperClasses)}>
          <input
            className="delivery-window__input"
            type="text"
            name="input-delivery-zip-code"
            id="input-delivery-zip-code"
            value={zipCode}
            placeholder="Zip Code"
            maxLength={5}
            onChange={handleZipCodeChange}
            ref={zipCodeInputRef}
          />
          <button
            className="delivery-window__input--cta-label type-eyebrow link"
            onClick={handleZipCodeCtaClick}
          >
            {ctaZipCode}
          </button>
        </div>
        <div className="delivery-window__input--alert">
          <p className="type-label">Please input a valid zip code</p>
        </div>
      </div>
      <div className={clsx(startDateWrapperClasses)}>
        <label
          className="delivery-window__label type-eyebrow"
          htmlFor="select-delivery-start-date"
        >
          Start Date
        </label>
        <div className="delivery-window__input-wrapper">
          <select
            className="delivery-window__input"
            name="select-delivery-start-date"
            id="select-delivery-start-date"
            disabled={!isZipCodeValid || !isZipCodeApplied}
            value={selectedStartDate?.date.format('YYYY-MM-DD') || ''}
            onChange={handleStartDateChange}
          >
            {visibleStartDates.map(({ date, label, isDisabled }) => (
              <option
                value={date.format('YYYY-MM-DD')}
                key={date.format('YYYY-MM-DD')}
                disabled={isDisabled}
              >
                {label}
              </option>
            ))}
          </select>
          <span className="delivery-window__input--cta-icon">
            <Icon name="select-arrow" />
          </span>
        </div>
      </div>
      {!isZipCodeValid && (
        <p className="delivery-window__description">
          {deliveryWindowDescription}
        </p>
      )}
      {exceptionNotification && (
        <p className="delivery-window__exception-notification">
          {exceptionNotification}
        </p>
      )}
      {weekDeliveryZone && selectedOptions.purchaseType && isZipCodeValid && (
        <DeliveryMessage
          template={deliveryMessageTemplate}
          deliveryZone={weekDeliveryZone}
          deliveries={deliveries}
          purchaseType={selectedOptions.purchaseType}
        />
      )}
    </div>
  )
}
